import { compareIso, endOfMonth, endOfYear, startOfMonth, startOfYear } from './dates.js'
import type { IsoDate } from './dates.js'
import { divideMinor } from './money.js'
import type { Minor } from './money.js'
import type { Balance, Bucket, Cadence, CategoryTotal, ItemType, PaymentView } from './types.js'

export interface Period {
  from: IsoDate
  to: IsoDate
}

export function monthPeriod(year: number, month: number): Period {
  return { from: startOfMonth(year, month), to: endOfMonth(year, month) }
}

export function yearPeriod(year: number): Period {
  return { from: startOfYear(year), to: endOfYear(year) }
}

function inPeriod(payment: PaymentView, period: Period): boolean {
  return (
    compareIso(payment.dueDate, period.from) >= 0 && compareIso(payment.dueDate, period.to) <= 0
  )
}

/** Płatności pominięte nie wchodzą do żadnego licznika. */
function counts(payment: PaymentView): boolean {
  return payment.status !== 'skipped'
}

const EMPTY: Bucket = { total: 0, paid: 0, pending: 0 }

function accumulate(payments: readonly PaymentView[]): Bucket {
  let paid = 0
  let pending = 0
  for (const payment of payments) {
    if (payment.status === 'paid') paid += payment.amountPlnMinor
    else pending += payment.amountPlnMinor
  }
  return { total: paid + pending, paid, pending }
}

function addBuckets(a: Bucket, b: Bucket): Bucket {
  return { total: a.total + b.total, paid: a.paid + b.paid, pending: a.pending + b.pending }
}

function scaleBucket(bucket: Bucket, factor: number): Bucket {
  return {
    total: bucket.total * factor,
    paid: bucket.paid * factor,
    pending: bucket.pending * factor,
  }
}

function divideBucket(bucket: Bucket, divisor: number): Bucket {
  return {
    total: divideMinor(bucket.total, divisor),
    paid: divideMinor(bucket.paid, divisor),
    pending: divideMinor(bucket.pending, divisor),
  }
}

export function bucketFor(
  payments: readonly PaymentView[],
  options: { cadence: Cadence; type: ItemType; period: Period },
): Bucket {
  const matching = payments.filter(
    (payment) =>
      counts(payment) &&
      payment.cadence === options.cadence &&
      payment.type === options.type &&
      inPeriod(payment, options.period),
  )
  return matching.length === 0 ? EMPTY : accumulate(matching)
}

/** Rzeczywiste miesięczne obciążenie: miesięczne + roczne ÷ 12 + jednorazowe z tego miesiąca. */
export function realMonthlyCost(
  payments: readonly PaymentView[],
  options: { year: number; month: number },
): Bucket {
  const month = monthPeriod(options.year, options.month)
  const year = yearPeriod(options.year)
  const monthly = bucketFor(payments, { cadence: 'monthly', type: 'expense', period: month })
  const yearly = bucketFor(payments, { cadence: 'yearly', type: 'expense', period: year })
  const oneOff = bucketFor(payments, { cadence: 'one_off', type: 'expense', period: month })
  return addBuckets(addBuckets(monthly, divideBucket(yearly, 12)), oneOff)
}

/**
 * Rzeczywisty koszt roku: roczne + miesięczne w ujęciu rocznym + jednorazowe z tego roku.
 *
 * Koszty miesięczne są annualizowane — sumę dzielimy przez liczbę miesięcy, w których
 * faktycznie wystąpiły płatności, i mnożymy przez dwanaście. Dzięki temu licznik pokazuje
 * to samo tempo wydatków niezależnie od tego, ile miesięcy zdążył pokryć harmonogram:
 * rok zmaterializowany w całości daje po prostu sumę swoich płatności.
 */
export function realYearlyCost(
  payments: readonly PaymentView[],
  options: { year: number },
): Bucket {
  const year = yearPeriod(options.year)
  const monthly = bucketFor(payments, { cadence: 'monthly', type: 'expense', period: year })
  const yearly = bucketFor(payments, { cadence: 'yearly', type: 'expense', period: year })
  const oneOff = bucketFor(payments, { cadence: 'one_off', type: 'expense', period: year })
  const monthlyAverage = divideBucket(monthly, monthlyOccurrenceCount(payments, year))
  return addBuckets(addBuckets(scaleBucket(monthlyAverage, 12), yearly), oneOff)
}

/** Liczba różnych miesięcy, w których wystąpiły płatności miesięczne — podstawa uśrednienia. */
function monthlyOccurrenceCount(payments: readonly PaymentView[], period: Period): number {
  const months = new Set<string>()
  for (const payment of payments) {
    if (!counts(payment)) continue
    if (payment.cadence !== 'monthly' || payment.type !== 'expense') continue
    if (!inPeriod(payment, period)) continue
    months.add(payment.dueDate.slice(0, 7))
  }
  return months.size === 0 ? 1 : months.size
}

export function balanceFor(payments: readonly PaymentView[], period: Period): Balance {
  let income = 0
  let expense = 0
  for (const payment of payments) {
    if (!counts(payment) || !inPeriod(payment, period)) continue
    if (payment.type === 'income') income += payment.amountPlnMinor
    else expense += payment.amountPlnMinor
  }
  return { income, expense, net: income - expense }
}

export function byCategory(
  payments: readonly PaymentView[],
  options: { type: ItemType; period: Period },
): CategoryTotal[] {
  const totals = new Map<string | null, Minor>()
  for (const payment of payments) {
    if (!counts(payment) || payment.type !== options.type) continue
    if (!inPeriod(payment, options.period)) continue
    totals.set(payment.categoryId, (totals.get(payment.categoryId) ?? 0) + payment.amountPlnMinor)
  }
  return [...totals.entries()]
    .map(([categoryId, total]) => ({ categoryId, total }))
    .sort((a, b) => b.total - a.total)
}
